import { Dispatch, SetStateAction, useEffect, useState, useTransition } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { InMemoryWebStorage, User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';
import Routes from 'shared/enums/routes';
import TokenProfile from 'shared/features/auth/authentication/models/token-profile';
import OidcConfig from 'shared/features/auth/authentication/oidc-config';
import { useBackchannelLogoutMutation } from 'shared/features/auth/authentication/queries/logout-queries';
import { clearAllActiveFilters } from 'shared/utils/clear-filters';
import { splitFullNameToInitials } from 'shared/utils/string-utils';

type AuthProfileTenant = {
  name: string;
  switchActivated: boolean;
  originalUser: User | undefined;
  setOriginalUser: Dispatch<SetStateAction<User | undefined>>;
  revertToDefaultTenant: () => Promise<boolean>;
  storeTenantUser: (user: User | null) => Promise<void>;
};

type UserDisplayData = {
  initials: string;
  fullname: string;
  tenantName: string;
};

export type AuthProfile = {
  initials: string | undefined;
  fullname: string | undefined;
  permissions: string[];
  logout: () => Promise<void>;
  backChannelLogout: () => void;
  backChannelLogoutIsLoading: boolean;
  tenant: AuthProfileTenant;
};

const REFRESH_ORIGINAL_USER_INTERVAL_MS = 60 * 1000 * 4;
const maintenanceHttpCode = 'ERR_NETWORK';
const maintenanceHttpStatusCode = 999;

const useAuthProfile = (): AuthProfile => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { mutateAsync: executeBackchannelLogout } = useBackchannelLogoutMutation();
  const { unauthenticatedInstance } = useAxiosClientsContextProvider();

  const [originalUser, setOriginalUser] = useState<User | undefined>();
  let authUserManager = undefined;

  const [backChannelLogoutIsLoading, startBackChannelLogoutTransition] = useTransition();
  const [tenantLoggingOutIsLoading, setTenantLoggingOutIsLoading] = useState(false);

  const [cachedUserData, setCachedUserData] = useState<UserDisplayData>({
    initials: '',
    fullname: '',
    tenantName: '',
  });

  const tokenProfile = auth.user?.profile as unknown as TokenProfile;
  const tenantSwitchActivated = tokenProfile?.customer_realm !== import.meta.env.VITE_TENANT_MANAGER_REALM_NAME;

  useEffect(() => {
    if (tokenProfile?.name && tokenProfile?.customer_realm_name && !backChannelLogoutIsLoading) {
      setCachedUserData({
        initials: splitFullNameToInitials(tokenProfile.name) || '',
        fullname: tokenProfile.name,
        tenantName: tokenProfile.customer_realm_name,
      });
    }
  }, [tokenProfile?.name, tokenProfile?.customer_realm_name, backChannelLogoutIsLoading]);

  const logoutChannel = new BroadcastChannel('logout');

  logoutChannel.onmessage = (messageEvent: MessageEvent<string>): void => {
    if (messageEvent.data === 'logout' && auth.isAuthenticated) {
      logout();
    }
  };

  auth.events.addSilentRenewError(async () => {
    const result = await callHealthCheck();

    const axiosError = result.error as AxiosError;
    if (axiosError?.code === maintenanceHttpCode || axiosError?.status === maintenanceHttpStatusCode) {
      globalThis.location.href = Routes.Login;
      return;
    }

    logout();
  });

  const callHealthCheck = async () => {
    try {
      const response = await unauthenticatedInstance.get('/health');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const logout = async (): Promise<void> => {
    const idToken = auth.user?.id_token;
    if (idToken) {
      await auth.signoutRedirect({
        id_token_hint: idToken,
        post_logout_redirect_uri: import.meta.env.VITE_OIDC_SIGNOUT_REDIRECT_URI,
      });
    }
  };

  const backChannelLogout = (): void => {
    startBackChannelLogoutTransition(async () => {
      await executeBackchannelLogout();
      logout();
    });

    if (tenantSwitchActivated) {
      setTenantLoggingOutIsLoading(true);
      revertToDefaultTenant();
    }
  };

  const revertToDefaultTenant = async (): Promise<boolean> => {
    try {
      const originalUserObj = originalUser ? new User({ ...originalUser }) : null;
      authUserManager = new UserManager(OidcConfig);
      await authUserManager.storeUser(originalUserObj);
      const user = await auth.signinSilent();

      if (user) {
        setOriginalUser(undefined);
        clearAllActiveFilters();
        return true;
      }

      return false;
    } catch {
      return false;
    }
  };

  const storeTenantUser = async (user: User | null): Promise<void> => {
    authUserManager = new UserManager(OidcConfig);
    await authUserManager.storeUser(user);
  };

  const refreshStashedUser = async () => {
    if (!originalUser) {
      return;
    }

    try {
      const tempUserManager = new UserManager({
        ...OidcConfig,
        userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
        automaticSilentRenew: false,
      });

      const originalUserObj = new User(originalUser);
      await tempUserManager.storeUser(originalUserObj);

      const refreshedUser = await tempUserManager.signinSilent();

      if (refreshedUser) {
        setOriginalUser(refreshedUser);
      }
    } catch (error) {
      console.error('Failed to refresh stashed user token:', error);
    }
  };

  useEffect(() => {
    if (!originalUser) {
      return;
    }

    const intervalId = setInterval(refreshStashedUser, REFRESH_ORIGINAL_USER_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [originalUser]);

  useEffect(() => {
    if (tenantLoggingOutIsLoading && !tenantSwitchActivated) {
      setTenantLoggingOutIsLoading(false);
      logout();
    }
  }, [tenantLoggingOutIsLoading, tenantSwitchActivated]);

  useEffect(() => {
    if (originalUser) {
      const originalUserObj = new User({ ...originalUser });

      if (auth.user?.access_token !== originalUserObj.access_token) {
        (async (): Promise<void> => {
          await storeTenantUser(originalUserObj ?? null);
          await auth.signinSilent();
        })();
        queryClient.clear();
        setOriginalUser(undefined);
      }
    }
  }, []);

  return {
    initials: cachedUserData.initials,
    fullname: cachedUserData.fullname,
    permissions: tokenProfile?.realm_roles?.permissions ?? [],
    logout,
    backChannelLogout,
    backChannelLogoutIsLoading,
    tenant: {
      name: cachedUserData.tenantName,
      switchActivated: tenantSwitchActivated,
      originalUser,
      setOriginalUser,
      revertToDefaultTenant,
      storeTenantUser,
    },
  };
};

export default useAuthProfile;
